<script setup>
import {computed, inject, onBeforeMount, ref} from "vue"
import {useStore} from "vuex"
import {useCan} from "@/utils/permissions"

const props = defineProps({
  user: Object
})

const loading = ref(true)

const trans = inject('trans')
const store = useStore()
const can = useCan()

const isAdmin = computed(() => store.getters["flowsDetails/isAdmin"])
const isPartner = computed(() => store.getters["flowsDetails/isPartner"])
const isTriggeredByWebhook = computed(() => store.getters["flowsDetails/isTriggeredByWebhook"])
const integrationId = computed(() => route().params.integration)
const integrationFlowId = computed(() => route().params.integrationFlow)
const tabs = computed(() => {
  let params = {
    user: props.user.id,
    integration: integrationId.value,
    integrationFlow: integrationFlowId.value,
  };

  return [
    {
      icon: 'overview-icon',
      title: trans('dashboard.integrations.flows.overview.title'),
      route: route('dashboard.user.integrations.flows.overview', params),
      active: route().current('dashboard.user.integrations.flows.overview'),
      hide: !can('flows.overview.view|flows.overview.update'),
    },
    {
      icon: 'settings-icon',
      title: trans('dashboard.integrations.flows.settings.title'),
      route: route('dashboard.user.integrations.flows.settings', params),
      active: route().current('dashboard.user.integrations.flows.settings'),
      hide: !can('flows.settings.view|flows.settings.update'),
    },
    {
      icon: 'business-rules-icon',
      title: trans('dashboard.integrations.flows.business_rules.title'),
      route: route('dashboard.user.integrations.flows.business_rules', params),
      active: route().current('dashboard.user.integrations.flows.business_rules'),
      hide: !can('flows.business-rules.view|flows.business-rules.update|flows.business-rules.create|flows.business-rules.delete'),
    },
    {
      icon: 'data-mapping-icon',
      title: trans('dashboard.integrations.flows.data_mapping.title'),
      route: route('dashboard.user.integrations.flows.data_mapping', params),
      active: route().current('dashboard.user.integrations.flows.data_mapping'),
      hide: !can('flows.data-mapping.view|flows.data-mapping.update'),
    },
    {
      icon: 'endpoints-icon',
      title: trans('dashboard.integrations.flows.endpoints.title'),
      route: route('dashboard.user.integrations.flows.endpoints', params),
      active: route().current('dashboard.user.integrations.flows.endpoints'),
      hide: !can('flows.endpoints.view|flows.endpoints.update'),
    },
    {
      icon: 'webhooks-icon',
      title: trans('dashboard.integrations.flows.webhooks.title'),
      route: route('dashboard.user.integrations.flows.webhooks', params),
      active: route().current('dashboard.user.integrations.flows.webhooks'),
      hide: !isTriggeredByWebhook.value || !can('flows.webhooks.view|flows.webhooks.update'),
    },
    {
      icon: 'filter-options-icon',
      title: trans('dashboard.integrations.flows.filter_options.title'),
      route: route('dashboard.user.integrations.flows.filter_options', params),
      active: route().current('dashboard.user.integrations.flows.filter_options'),
      hide: !can('flows.filter-options.view|flows.filter-options.update'),
    },
    {
      icon: 'activity-icon',
      title: trans('dashboard.integrations.flows.activity.title'),
      route: route('dashboard.user.integrations.flows.activity', params),
      active: route().current('dashboard.user.integrations.flows.activity'),
      hide: !can('flows.activity-logs.view'),
    },
    {
      icon: 'admin-icon',
      title: trans('dashboard.integrations.flows.admin.title'),
      route: route('dashboard.user.integrations.flows.admin', params),
      active: route().current('dashboard.user.integrations.flows.admin'),
      hide: !isAdmin.value && !isPartner.value,
    },
    {
      icon: 'change-logs-icon',
      title: trans('dashboard.integrations.flows.change_logs.title'),
      route: route('dashboard.user.integrations.flows.change-logs', params),
      active: route().current('dashboard.user.integrations.flows.change-logs'),
      hide: !can('flows.change-logs.view'),
    },
  ];
})

onBeforeMount(() => axios
  .get(`/user/${props.user.id}/user-integration/${integrationId.value}/flows/${integrationFlowId.value}`)
  .then(({data}) => store.dispatch("flowsDetails/storeFlowsData", {...data.data}))
  .finally(() => loading.value = false)
)
</script>
<template>
  <div class="mb-8">
    <div class="tile tabs mx-6 mt-4">
      <div class="flex">
        <inertia-link
          v-for="(tab, index) in tabs"
          :key="index"
          :hidden="tab.hide"
          :href="tab.route"
          class="w-1/6 pl-8 pt-2 pb-3 tab"
          :class="{ tab_active: tab.active }"
        >
          <svg class="icon tab_icon">
            <use :xlink:href="`#${tab.icon}`"></use>
          </svg>
          {{ tab.title }}
        </inertia-link>
      </div>
    </div>
    <div class="mx-6">
      <loading-view :loading="loading">
        <slot/>
      </loading-view>
    </div>
  </div>
</template>
